import React, {lazy, Suspense, useEffect} from 'react';
import './App.css';
import './components/forms_components/styleFormManager.css';
import {QueryClient, QueryClientProvider} from "react-query";
import {ThemeProvider} from "styled-components";
import TimedTheme from "./assets/styles/TimedTheme";
import GlobalStyle from './assets/styles/Global';
import {Navigate, Route, Routes, useLocation} from "react-router-dom";
import PrivateRoute from "./components/security/PrivateRoute";
import PublicRoute from "./components/security/PublicRoute";
import {ToastContainer} from "react-toastify";
import Login from "./Pages/Login/Login";
import {ViewportProvider} from "./services/providers/ViewportProvider";
import PwdRetrieve from "./Pages/PwdRetrieve/PwdRetrieve";
import { ReactQueryDevtools } from "react-query/devtools";
import TimedSuspense2 from "./components/organisms/layouts/TimedSuspense2";
import RuntimeErrorBoundary from "./components/RuntimeErrorBoundary";
import { logRuntimeDiagnostic } from "./services/diagRuntime";

const ApplicationController = lazy(()=>import("./Pages/ApplicationController"))

export const queryClient = new QueryClient({
    defaultOptions:{
        queries:{
            suspense:true,
            refetchOnWindowFocus:false
        }
    }
});

function App() {
  const {pathname} = useLocation();

  useEffect(() => {
      const bootedAt = Date.now();
      const sessionBootKey = "__timed_session_boot_count__";
      const installBootKey = "__timed_install_boot_count__";
      let sessionBootCount = 1;
      let installBootCount = 1;

      try {
          sessionBootCount = Number(window.sessionStorage.getItem(sessionBootKey) || "0") + 1;
          window.sessionStorage.setItem(sessionBootKey, String(sessionBootCount));
      } catch (error) {
          console.warn("[runtime-diag] failed to update session boot count", error);
      }

      try {
          installBootCount = Number(window.localStorage.getItem(installBootKey) || "0") + 1;
          window.localStorage.setItem(installBootKey, String(installBootCount));
      } catch (error) {
          console.warn("[runtime-diag] failed to update install boot count", error);
      }

      const navigationEntry = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
      const navigationType = navigationEntry?.type || "unknown";
      const bootDetail = [
          `sessionBoot=${sessionBootCount}`,
          `installBoot=${installBootCount}`,
          `nav=${navigationType}`,
          `ready=${document.readyState}`,
          `ref=${document.referrer ? "1" : "0"}`,
      ].join(",");

      void logRuntimeDiagnostic("boot-start", bootDetail, {
          sessionBootCount,
          installBootCount,
          navigationType,
          referrer: document.referrer || null,
      });

      const logUnload = (kind: "pagehide" | "beforeunload", persisted?: boolean) => {
          const detail = [
              `ageMs=${Date.now() - bootedAt}`,
              `sessionBoot=${sessionBootCount}`,
              `installBoot=${installBootCount}`,
              `persisted=${persisted === undefined ? "na" : persisted ? "1" : "0"}`,
              `vis=${document.visibilityState}`,
          ].join(",");

          void logRuntimeDiagnostic(kind, detail, {
              ageMs: Date.now() - bootedAt,
              sessionBootCount,
              installBootCount,
              persisted,
          });
      };

      const onPageHide = (event: PageTransitionEvent) => {
          logUnload("pagehide", event.persisted);
      };

      const onBeforeUnload = () => {
          logUnload("beforeunload");
      };

      window.addEventListener("pagehide", onPageHide);
      window.addEventListener("beforeunload", onBeforeUnload);

      return () => {
          window.removeEventListener("pagehide", onPageHide);
          window.removeEventListener("beforeunload", onBeforeUnload);
      };
  }, []);

  useEffect(() => {
      if (!pathname.startsWith("/timed_application")) return;

      document.body.dataset.appBooting = "1";
      delete document.body.dataset.pageVisible;

      const timeoutId = window.setTimeout(() => {
          if (document.body.dataset.pageVisible === "1") return;

          const root = document.getElementById("root");
          const childCount = root?.childElementCount ?? 0;
          const textSize = root?.textContent?.trim().length ?? 0;
          const detail = [
              "timeout",
              `ready=${document.readyState}`,
              `vis=${document.visibilityState}`,
              `child=${childCount}`,
              `text=${textSize}`,
          ].join(",");

          void logRuntimeDiagnostic("blank-suspect", detail);
      }, 8000);

      return () => {
          window.clearTimeout(timeoutId);
      };
  }, [pathname]);

  return (
      <RuntimeErrorBoundary>
          <ThemeProvider theme={TimedTheme}>
            <GlobalStyle />
            <QueryClientProvider client={queryClient}>
                <ViewportProvider>
                  <div className="App">
                      <Routes>
                          <Route element={<PrivateRoute from={"App"}/>}>
                              <Route path="/timed_application/*" element={
                                  <Suspense fallback={<TimedSuspense2 text={"Chargement de l'application"}/> }>
                                    <ApplicationController />
                                  </Suspense>
                              } />
                          </Route>
                          <Route element={<PublicRoute/>}>
                              <Route path="/" element={<Navigate to="/login" />} />
                              <Route path="/login" element={<Login />} />
                              <Route path="/pwd_retrieve" element={<PwdRetrieve />} />
                          </Route>

                          {/*<Route path="*" element={<p>Non trouvÃ©</p>} />*/}
                      </Routes>
                      <ToastContainer
                          position="bottom-right"
                          autoClose={5000}
                          hideProgressBar={false}
                          newestOnTop={false}
                          closeOnClick
                          rtl={false}
                          pauseOnFocusLoss
                          draggable
                          pauseOnHover
                      />
                  </div>
                </ViewportProvider>
                <ReactQueryDevtools initialIsOpen={false} />
            </QueryClientProvider>
          </ThemeProvider>
      </RuntimeErrorBoundary>
  );
}

export default App;
